import { MOMENT_KINDS } from "@domain/conversation-intelligence"
import type { Dataset, DatasetColumn, DatasetRow } from "@domain/datasets"
import {
  addColumn,
  addTracesToDataset,
  createDataset,
  createDatasetFromTraces,
  DATASET_LIST_SORT_COLUMNS,
  DatasetRepository,
  type DeleteRowsSelection,
  deleteDataset,
  deleteRows,
  getRowDetail,
  insertRows,
  listDatasets,
  listRows,
  MAX_TRACES_PER_DATASET_IMPORT,
  parseDatasetCsv,
  prepareDatasetExportUseCase,
  removeColumn,
  reorderColumns,
  restoreColumn,
  searchDatasets,
  type TraceSelection,
  type TraceSource,
  updateColumn,
  updateDatasetDetails,
  updateRow,
} from "@domain/datasets"
import {
  DatasetId,
  DatasetRowId,
  DatasetVersionId,
  filterSetSchema,
  isValidId,
  type OrganizationId,
  ProjectId,
  putInDisk,
  SignalId,
  sortDirectionSchema,
  TaxonomyClusterId,
  TraceId,
  UnauthorizedError,
} from "@domain/shared"
import { listClusterSessionTraceIdsUseCase } from "@domain/taxonomy"
import { AIEmbedLive, withAi } from "@platform/ai"
import {
  DatasetRowRepositoryLive,
  ScoreAnalyticsRepositoryLive,
  TaxonomyClusterIntelligenceRepositoryLive,
  TraceRepositoryLive,
} from "@platform/db-clickhouse"
import { DatasetRepositoryLive, OutboxEventWriterLive, TaxonomyClusterRepositoryLive } from "@platform/db-postgres"
import { withTracing } from "@repo/observability"
import { createServerFn } from "@tanstack/react-start"
import { Effect, Layer } from "effect"
import { z } from "zod"
import { enforceExportRequestRateLimit } from "../../domains/exports/export-rate-limit.ts"
import { ensureSession } from "../../domains/sessions/session.functions.ts"
import { getSessionOrganizationId, requireSession } from "../../server/auth.ts"
import {
  getClickhouseClient,
  getPostgresClient,
  getQueuePublisher,
  getRedisClient,
  getStorageDisk,
} from "../../server/clients.ts"
import { resolveOrgScope } from "../../server/resolve-org-scope.ts"
import { withScopedClickHouse } from "../../server/scoped-clickhouse.ts"
import { withScopedPostgres } from "../../server/scoped-postgres.ts"
import { applyMapping } from "./column-mapping.ts"

const rowSelectionSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("selected"), rowIds: z.array(z.string()).min(1) }),
  z.object({ mode: z.literal("all") }),
  z.object({ mode: z.literal("allExcept"), rowIds: z.array(z.string()) }),
])

const saveDatasetCsvDataSchema = z.object({
  datasetId: z.string().min(1),
  projectId: z.string().min(1),
  mapping: z.object({
    input: z.array(z.string()),
    output: z.array(z.string()),
    expectedOutput: z.array(z.string()),
    metadata: z.array(z.string()),
  }),
  options: z
    .object({
      flattenSingleColumn: z.boolean(),
      autoParseJson: z.boolean(),
    })
    .default({ flattenSingleColumn: false, autoParseJson: false }),
})

type SaveDatasetCsvData = z.infer<typeof saveDatasetCsvDataSchema>

export interface DatasetRecord {
  readonly id: string
  readonly organizationId: string
  readonly projectId: string
  readonly slug: string
  readonly name: string
  readonly description: string | null
  readonly fileKey: string | null
  readonly columns: DatasetColumn[] | null
  readonly currentVersion: number
  readonly latestVersionId: string | null
  readonly createdAt: string
  readonly updatedAt: string
}

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }

export interface DatasetRowRecord {
  readonly rowId: string
  readonly datasetId: string
  readonly input: string | Record<string, JsonValue>
  readonly output: string | Record<string, JsonValue>
  readonly expectedOutput: string | Record<string, JsonValue>
  readonly metadata: string | Record<string, JsonValue>
  readonly custom: Record<string, string | Record<string, JsonValue>>
  readonly createdAt: string
  readonly version: number
}

const toDatasetRecord = (d: Dataset): DatasetRecord => ({
  id: d.id,
  organizationId: d.organizationId,
  projectId: d.projectId,
  slug: d.slug,
  name: d.name,
  description: d.description,
  fileKey: d.fileKey,
  columns: d.columns,
  currentVersion: d.currentVersion,
  latestVersionId: d.latestVersionId,
  createdAt: d.createdAt.toISOString(),
  updatedAt: d.updatedAt.toISOString(),
})

const toCell = (v: DatasetRow["input"]): string | Record<string, JsonValue> =>
  typeof v === "string" ? v : (v as Record<string, JsonValue>)

const toRowRecord = (r: DatasetRow): DatasetRowRecord => ({
  rowId: r.rowId,
  datasetId: r.datasetId,
  input: toCell(r.input),
  output: toCell(r.output),
  expectedOutput: toCell(r.expectedOutput),
  metadata: toCell(r.metadata),
  custom: Object.fromEntries(Object.entries(r.custom).map(([k, v]) => [k, toCell(v)])),
  createdAt: r.createdAt.toISOString(),
  version: r.version,
})

const datasetListCursorSchema = z.object({
  sortValue: z.string(),
  id: z.string(),
})

interface DatasetListResult {
  readonly datasets: readonly DatasetRecord[]
  readonly hasMore: boolean
  readonly nextCursor?: { readonly sortValue: string; readonly id: string }
}

export const listDatasetsByProject = createServerFn({ method: "GET" })
  .inputValidator(
    z
      .object({
        projectId: z.string(),
        limit: z.number().int().min(1).max(500).optional(),
        cursor: datasetListCursorSchema.optional(),
        sortBy: z.enum(DATASET_LIST_SORT_COLUMNS).optional(),
        sortDirection: sortDirectionSchema.optional(),
      })
      .refine(
        (data) => {
          if (!data.cursor) return true
          const sortBy = data.sortBy ?? "updatedAt"
          if (sortBy === "updatedAt") {
            const date = new Date(data.cursor.sortValue)
            return !Number.isNaN(date.getTime()) && date.toISOString() === data.cursor.sortValue
          }
          return true
        },
        {
          message: "cursor.sortValue must be a valid ISO date string when sortBy is 'updatedAt'",
          path: ["cursor", "sortValue"],
        },
      ),
  )
  .handler(async ({ data, context }): Promise<DatasetListResult> => {
    const orgId = await resolveOrgScope(context)

    const page = await Effect.runPromise(
      listDatasets({
        projectId: ProjectId(data.projectId),
        options: {
          limit: data.limit ?? 50,
          ...(data.cursor ? { cursor: data.cursor } : {}),
          ...(data.sortBy ? { sortBy: data.sortBy } : {}),
          ...(data.sortDirection ? { sortDirection: data.sortDirection } : {}),
        },
      }).pipe(withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId), withTracing),
    )

    const datasets = page.datasets.map(toDatasetRecord)
    if (!page.nextCursor) {
      return { datasets, hasMore: page.hasMore }
    }
    return { datasets, hasMore: page.hasMore, nextCursor: page.nextCursor }
  })

export interface DatasetSearchRecord {
  readonly id: string
  readonly projectId: string
  readonly projectSlug: string
  readonly projectName: string
  readonly slug: string
  readonly name: string
}

/**
 * Org-wide dataset search for the Command Palette. Unlike {@link listDatasetsByProject}, this is
 * not scoped to a single project — it returns matching datasets across every project in the
 * caller's organization, each tagged with its owning project's slug/name.
 */
export const searchDatasetsOrgWide = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      searchQuery: z.string().max(500).optional(),
      preferProjectId: z.string().optional(),
      limit: z.number().int().min(1).max(25).optional(),
    }),
  )
  .handler(async ({ data, context }): Promise<readonly DatasetSearchRecord[]> => {
    const orgId = await resolveOrgScope(context)

    const results = await Effect.runPromise(
      searchDatasets({
        ...(data.searchQuery !== undefined ? { searchQuery: data.searchQuery } : {}),
        ...(data.preferProjectId !== undefined ? { preferProjectId: ProjectId(data.preferProjectId) } : {}),
        ...(data.limit !== undefined ? { limit: data.limit } : {}),
      }).pipe(withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId), withTracing),
    )

    return results.map(
      (r): DatasetSearchRecord => ({
        id: r.id,
        projectId: r.projectId,
        projectSlug: r.projectSlug,
        projectName: r.projectName,
        slug: r.slug,
        name: r.name,
      }),
    )
  })

export const getDatasetQuery = createServerFn({ method: "GET" })
  .inputValidator(z.object({ datasetId: z.string() }))
  .handler(async ({ data, context }): Promise<DatasetRecord | null> => {
    const orgId = await resolveOrgScope(context)

    return Effect.runPromise(
      Effect.gen(function* () {
        const repo = yield* DatasetRepository
        const dataset = yield* repo.findById(DatasetId(data.datasetId))
        return toDatasetRecord(dataset)
      }).pipe(
        Effect.catchTag("DatasetNotFoundError", () => Effect.succeed(null)),
        withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
        withTracing,
      ),
    )
  })

const listRowsCursorSchema = z.object({
  createdAt: z.string(),
  rowId: z.string(),
})

const DATASET_ROW_SORT_COLUMNS = ["createdAt"] as const

export const listRowsQuery = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      datasetId: z.string(),
      versionId: z.string().optional(),
      search: z.string().optional(),
      sortBy: z.enum(DATASET_ROW_SORT_COLUMNS).optional(),
      sortDirection: sortDirectionSchema.optional(),
      limit: z.number().int().min(1).max(500).default(50),
      offset: z.number().default(0),
      cursor: listRowsCursorSchema.optional(),
    }),
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<{
      rows: DatasetRowRecord[]
      total?: number
      nextCursor?: { createdAt: string; rowId: string }
    }> => {
      const orgId = await resolveOrgScope(context)

      const sortBy = data.sortBy ?? "createdAt"
      const sortDirection = data.sortDirection ?? "desc"
      const sortDirectionForRows = sortBy === "createdAt" ? sortDirection : "desc"

      const result = await Effect.runPromise(
        listRows({
          datasetId: DatasetId(data.datasetId),
          ...(data.versionId ? { versionId: DatasetVersionId(data.versionId) } : {}),
          ...(data.search ? { search: data.search } : {}),
          sortDirection: sortDirectionForRows,
          limit: data.limit,
          ...(data.cursor
            ? {
                cursor: {
                  createdAt: data.cursor.createdAt,
                  rowId: DatasetRowId(data.cursor.rowId),
                },
              }
            : { offset: data.offset }),
        }).pipe(
          withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
          withScopedClickHouse(DatasetRowRepositoryLive, getClickhouseClient(), orgId),
          withTracing,
        ),
      )

      return {
        rows: result.rows.map(toRowRecord),
        ...(result.total !== undefined ? { total: result.total } : {}),
        ...(result.nextCursor ? { nextCursor: result.nextCursor } : {}),
      }
    },
  )

export const getRowQuery = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      datasetId: z.string(),
      rowId: z.string(),
      versionId: z.string().optional(),
    }),
  )
  .handler(async ({ data, context }): Promise<DatasetRowRecord | null> => {
    const orgId = await resolveOrgScope(context)

    return Effect.runPromise(
      getRowDetail({
        datasetId: DatasetId(data.datasetId),
        rowId: DatasetRowId(data.rowId),
        ...(data.versionId ? { versionId: DatasetVersionId(data.versionId) } : {}),
      }).pipe(
        Effect.map(toRowRecord),
        Effect.catchTag("RowNotFoundError", () => Effect.succeed(null)),
        withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
        withScopedClickHouse(DatasetRowRepositoryLive, getClickhouseClient(), orgId),
        withTracing,
      ),
    )
  })

export const updateDataset = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      datasetId: z.string(),
      name: z.string(),
      description: z.string().nullable().optional(),
    }),
  )
  .handler(async ({ data, context }): Promise<DatasetRecord> => {
    const orgId = await resolveOrgScope(context)

    const dataset = await Effect.runPromise(
      updateDatasetDetails({
        datasetId: DatasetId(data.datasetId),
        name: data.name,
        description: data.description ?? null,
      }).pipe(withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId), withTracing),
    )

    return toDatasetRecord(dataset)
  })

export const deleteDatasetFunction = createServerFn({ method: "POST" })
  .inputValidator(z.object({ datasetId: z.string() }))
  .handler(async ({ data, context }): Promise<void> => {
    const orgId = await resolveOrgScope(context)

    await Effect.runPromise(
      deleteDataset({
        datasetId: DatasetId(data.datasetId),
      }).pipe(withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId), withTracing),
    )
  })

type DownloadDatasetExportResult =
  | { readonly type: "direct"; readonly csv: string; readonly filename: string }
  | { readonly type: "enqueued" }

export const downloadDatasetExport = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      datasetId: z.string(),
      selection: rowSelectionSchema,
    }),
  )
  .handler(async ({ data, context }): Promise<DownloadDatasetExportResult> => {
    const session = await ensureSession()
    const email = session?.user?.email
    const organizationId = getSessionOrganizationId(session)

    if (!organizationId || !email) {
      throw new UnauthorizedError({ message: "Unauthorized" })
    }

    const orgId = await resolveOrgScope(context)
    const datasetId = DatasetId(data.datasetId)

    const result = await Effect.runPromise(
      prepareDatasetExportUseCase({
        datasetId,
        selection: data.selection,
        organizationId: orgId,
        recipientEmail: email,
      }).pipe(
        withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
        withScopedClickHouse(DatasetRowRepositoryLive, getClickhouseClient(), orgId),
        withTracing,
      ),
    )

    if (result.kind === "direct") {
      return { type: "direct" as const, csv: result.csv, filename: result.filename }
    }

    // needsEnqueue — only here do we pay the queue/rate-limit cost.
    await enforceExportRequestRateLimit({
      redis: getRedisClient(),
      organizationId,
      projectId: result.payload.projectId,
      recipientEmail: email,
    })

    const publisher = await getQueuePublisher()
    await Effect.runPromise(publisher.publish("exports", "generate", result.payload))

    return { type: "enqueued" as const }
  })

export const createDatasetFunction = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      id: z
        .string()
        .optional()
        .refine((value) => value === undefined || isValidId(value), {
          message: "Invalid dataset id",
        }),
      projectId: z.string(),
      name: z.string(),
    }),
  )
  .handler(async ({ data, context }): Promise<DatasetRecord> => {
    const { userId } = await requireSession()
    const orgId = await resolveOrgScope(context)

    const dataset = await Effect.runPromise(
      createDataset({
        ...(data.id ? { id: DatasetId(data.id) } : {}),
        projectId: ProjectId(data.projectId),
        name: data.name,
        actorUserId: userId,
      }).pipe(
        withScopedPostgres(Layer.mergeAll(DatasetRepositoryLive, OutboxEventWriterLive), getPostgresClient(), orgId),
        withScopedClickHouse(DatasetRowRepositoryLive, getClickhouseClient(), orgId),
        withTracing,
      ),
    )

    return toDatasetRecord(dataset)
  })

export const saveDatasetCsv = createServerFn({ method: "POST" })
  .inputValidator((input: unknown): { file: File; data: SaveDatasetCsvData } => {
    if (!(input instanceof FormData)) throw new Error("Expected FormData")
    const file = input.get("file")
    const dataRaw = input.get("data")
    if (!(file instanceof File)) throw new Error("No file provided")
    if (typeof dataRaw !== "string") throw new Error("No data provided")
    const data = saveDatasetCsvDataSchema.parse(JSON.parse(dataRaw))
    return { file, data }
  })
  .handler(async ({ data: { file, data }, context }): Promise<{ version: number; rowCount: number }> => {
    const orgId = await resolveOrgScope(context)
    const { datasetId, projectId, mapping, options } = data

    const content = await file.text()

    const fileKey = await Effect.runPromise(
      putInDisk(getStorageDisk(), {
        namespace: "datasets",
        organizationId: orgId,
        projectId: ProjectId(projectId),
        content,
      }).pipe(withTracing),
    )

    await Effect.runPromise(
      Effect.gen(function* () {
        const repo = yield* DatasetRepository
        return yield* repo.updateFileKey({
          id: DatasetId(datasetId),
          fileKey,
        })
      }).pipe(withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId), withTracing),
    )

    const { rows } = parseDatasetCsv(content)
    const mappedRows = rows.map((row) => applyMapping(row, mapping, options))

    if (mappedRows.length === 0) {
      return { version: 0, rowCount: 0 }
    }

    const result = await Effect.runPromise(
      insertRows({
        datasetId: DatasetId(datasetId),
        rows: mappedRows,
        source: "csv",
      }).pipe(
        withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
        withScopedClickHouse(DatasetRowRepositoryLive, getClickhouseClient(), orgId),
        withTracing,
      ),
    )

    return { version: result.version, rowCount: mappedRows.length }
  })

export const insertDatasetRow = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      datasetId: z.string(),
      input: z.string(),
      output: z.string(),
      expectedOutput: z.string(),
      metadata: z.string(),
      custom: z.record(z.string(), z.string()).optional(),
    }),
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<{
      readonly versionId: string
      readonly version: number
      readonly rowId: string
    }> => {
      const orgId = await resolveOrgScope(context)

      const result = await Effect.runPromise(
        insertRows({
          datasetId: DatasetId(data.datasetId),
          rows: [
            {
              input: data.input,
              output: data.output,
              expectedOutput: data.expectedOutput,
              metadata: data.metadata,
              ...(data.custom ? { custom: data.custom } : {}),
            },
          ],
          source: "web",
        }).pipe(
          withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
          withScopedClickHouse(DatasetRowRepositoryLive, getClickhouseClient(), orgId),
          withTracing,
        ),
      )

      const rowId = result.rowIds[0]
      if (rowId === undefined) {
        throw new Error("insertRows returned no row id")
      }

      return {
        versionId: result.versionId as string,
        version: result.version,
        rowId: rowId as string,
      }
    },
  )

export const updateDatasetRow = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      datasetId: z.string(),
      rowId: z.string(),
      input: z.string(),
      output: z.string(),
      expectedOutput: z.string(),
      metadata: z.string(),
      custom: z.record(z.string(), z.string()).optional(),
    }),
  )
  .handler(async ({ data, context }) => {
    const orgId = await resolveOrgScope(context)

    const result = await Effect.runPromise(
      updateRow({
        datasetId: DatasetId(data.datasetId),
        rowId: DatasetRowId(data.rowId),
        input: data.input,
        output: data.output,
        expectedOutput: data.expectedOutput,
        metadata: data.metadata,
        ...(data.custom ? { custom: data.custom } : {}),
      }).pipe(
        withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
        withScopedClickHouse(DatasetRowRepositoryLive, getClickhouseClient(), orgId),
        withTracing,
      ),
    )

    return { versionId: result.versionId, version: result.version }
  })

export const deleteDatasetRows = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      datasetId: z.string(),
      selection: rowSelectionSchema,
    }),
  )
  .handler(async ({ data, context }): Promise<{ versionId: string; version: number }> => {
    const orgId = await resolveOrgScope(context)

    const selection: DeleteRowsSelection =
      data.selection.mode === "all"
        ? { mode: "all" }
        : {
            mode: data.selection.mode,
            rowIds: data.selection.rowIds.map(DatasetRowId),
          }

    const result = await Effect.runPromise(
      deleteRows({
        datasetId: DatasetId(data.datasetId),
        selection,
      }).pipe(
        withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
        withScopedClickHouse(DatasetRowRepositoryLive, getClickhouseClient(), orgId),
        withTracing,
      ),
    )

    return { versionId: result.versionId as string, version: result.version }
  })

function toTraceSelection(sel: z.infer<typeof rowSelectionSchema>): TraceSelection {
  if (sel.mode === "all") return { mode: "all" }
  return { mode: sel.mode, traceIds: sel.rowIds.map(TraceId) }
}

function toTraceSource(signalId: string | undefined): TraceSource {
  return signalId ? { kind: "issue", signalId: SignalId(signalId) } : { kind: "project" }
}

export const addTracesToDatasetFunction = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      projectId: z.string(),
      datasetId: z.string(),
      signalId: z.string().optional(),
      selection: rowSelectionSchema,
      searchQuery: z.string().max(500).optional(),
      filters: filterSetSchema.optional(),
    }),
  )
  .handler(async ({ data, context }): Promise<{ versionId: string; version: number; rowCount: number }> => {
    const orgId = await resolveOrgScope(context)
    const chClient = getClickhouseClient()

    const result = await Effect.runPromise(
      addTracesToDataset({
        projectId: ProjectId(data.projectId),
        datasetId: DatasetId(data.datasetId),
        source: toTraceSource(data.signalId),
        selection: toTraceSelection(data.selection),
        ...(data.searchQuery ? { searchQuery: data.searchQuery } : {}),
        ...(data.filters ? { filters: data.filters } : {}),
      }).pipe(
        withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
        withScopedClickHouse(DatasetRowRepositoryLive, chClient, orgId),
        withScopedClickHouse(TraceRepositoryLive, chClient, orgId),
        withScopedClickHouse(ScoreAnalyticsRepositoryLive, chClient, orgId),
        withAi(AIEmbedLive, getRedisClient()),
        withTracing,
      ),
    )

    return {
      versionId: result.versionId as string,
      version: result.version,
      rowCount: result.rowIds.length,
    }
  })

export const createDatasetFromTracesFunction = createServerFn({
  method: "POST",
})
  .inputValidator(
    z.object({
      datasetId: z
        .string()
        .optional()
        .refine((value) => value === undefined || isValidId(value), {
          message: "Invalid dataset id",
        }),
      projectId: z.string(),
      signalId: z.string().optional(),
      name: z.string().min(1),
      selection: rowSelectionSchema,
      searchQuery: z.string().max(500).optional(),
      filters: filterSetSchema.optional(),
    }),
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<{
      datasetId: string
      versionId: string
      version: number
      rowCount: number
    }> => {
      const orgId = await resolveOrgScope(context)
      const pgClient = getPostgresClient()
      const chClient = getClickhouseClient()

      const result = await Effect.runPromise(
        createDatasetFromTraces({
          ...(data.datasetId ? { datasetId: DatasetId(data.datasetId) } : {}),
          projectId: ProjectId(data.projectId),
          name: data.name,
          source: toTraceSource(data.signalId),
          selection: toTraceSelection(data.selection),
          ...(data.searchQuery ? { searchQuery: data.searchQuery } : {}),
          ...(data.filters ? { filters: data.filters } : {}),
        }).pipe(
          withScopedPostgres(Layer.mergeAll(DatasetRepositoryLive, OutboxEventWriterLive), pgClient, orgId),
          withScopedClickHouse(DatasetRowRepositoryLive, chClient, orgId),
          withScopedClickHouse(TraceRepositoryLive, chClient, orgId),
          withScopedClickHouse(ScoreAnalyticsRepositoryLive, chClient, orgId),
          withAi(AIEmbedLive, getRedisClient()),
          withTracing,
        ),
      )

      return {
        datasetId: result.datasetId as string,
        versionId: result.versionId as string,
        version: result.version,
        rowCount: result.rowIds.length,
      }
    },
  )

const clusterSourceSchema = z.object({
  clusterId: z.string(),
  filter: z.enum(["all", ...MOMENT_KINDS]).optional(),
  momentRange: z
    .object({
      metric: z.enum(["frequency", "escalation", "resolution", "churnRisk", "wins"]),
      fromTurn: z.number().int().min(0),
      toTurn: z.number().int().min(0),
    })
    .optional(),
  timeFromIso: z.string().optional(),
  timeToIso: z.string().optional(),
})

export type ClusterSource = z.infer<typeof clusterSourceSchema>
type ClusterSourceInput = ClusterSource

// Resolve a behaviour selection to explicit trace ids. "selected" uses the
// checked rows verbatim (the table already holds their trace ids); "all" /
// "allExcept" resolve the cluster (its subtree) to one trace id per session
// honouring the drawer's active filters, capped one above the import limit so
// the domain surfaces TooManyTracesError when the selection is too large.
function resolveClusterTraceIds(
  orgId: OrganizationId,
  projectId: ProjectId,
  cluster: ClusterSourceInput,
  selection: z.infer<typeof rowSelectionSchema>,
) {
  return Effect.gen(function* () {
    if (selection.mode === "selected") return selection.rowIds.map(TraceId)
    const all = yield* listClusterSessionTraceIdsUseCase({
      organizationId: orgId,
      projectId,
      clusterId: TaxonomyClusterId(cluster.clusterId),
      ...(cluster.filter ? { filter: cluster.filter } : {}),
      ...(cluster.momentRange ? { momentRange: cluster.momentRange } : {}),
      ...(cluster.timeFromIso ? { startTimeFrom: new Date(cluster.timeFromIso) } : {}),
      ...(cluster.timeToIso ? { startTimeTo: new Date(cluster.timeToIso) } : {}),
      limit: MAX_TRACES_PER_DATASET_IMPORT + 1,
    })
    if (selection.mode === "all") return all
    const excluded = new Set<string>(selection.rowIds)
    return all.filter((id) => !excluded.has(id as string))
  })
}

export const addClusterSessionsToDatasetFunction = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      projectId: z.string(),
      datasetId: z.string(),
      cluster: clusterSourceSchema,
      selection: rowSelectionSchema,
    }),
  )
  .handler(async ({ data, context }): Promise<{ versionId: string; version: number; rowCount: number }> => {
    const orgId = await resolveOrgScope(context)
    const projectId = ProjectId(data.projectId)
    const chClient = getClickhouseClient()

    const result = await Effect.runPromise(
      Effect.gen(function* () {
        const traceIds = yield* resolveClusterTraceIds(orgId, projectId, data.cluster, data.selection)
        return yield* addTracesToDataset({
          projectId,
          datasetId: DatasetId(data.datasetId),
          source: { kind: "project" },
          selection: { mode: "selected", traceIds },
        })
      }).pipe(
        withScopedPostgres(
          Layer.mergeAll(DatasetRepositoryLive, TaxonomyClusterRepositoryLive),
          getPostgresClient(),
          orgId,
        ),
        withScopedClickHouse(
          Layer.mergeAll(
            DatasetRowRepositoryLive,
            TraceRepositoryLive,
            ScoreAnalyticsRepositoryLive,
            TaxonomyClusterIntelligenceRepositoryLive,
          ),
          chClient,
          orgId,
        ),
        withAi(AIEmbedLive, getRedisClient()),
        withTracing,
      ),
    )

    return {
      versionId: result.versionId as string,
      version: result.version,
      rowCount: result.rowIds.length,
    }
  })

export const createDatasetFromClusterSessionsFunction = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      projectId: z.string(),
      name: z.string().min(1),
      cluster: clusterSourceSchema,
      selection: rowSelectionSchema,
    }),
  )
  .handler(
    async ({ data, context }): Promise<{ datasetId: string; versionId: string; version: number; rowCount: number }> => {
      const orgId = await resolveOrgScope(context)
      const projectId = ProjectId(data.projectId)
      const chClient = getClickhouseClient()

      const result = await Effect.runPromise(
        Effect.gen(function* () {
          const traceIds = yield* resolveClusterTraceIds(orgId, projectId, data.cluster, data.selection)
          return yield* createDatasetFromTraces({
            projectId,
            name: data.name,
            source: { kind: "project" },
            selection: { mode: "selected", traceIds },
          })
        }).pipe(
          withScopedPostgres(
            Layer.mergeAll(DatasetRepositoryLive, OutboxEventWriterLive, TaxonomyClusterRepositoryLive),
            getPostgresClient(),
            orgId,
          ),
          withScopedClickHouse(
            Layer.mergeAll(
              DatasetRowRepositoryLive,
              TraceRepositoryLive,
              ScoreAnalyticsRepositoryLive,
              TaxonomyClusterIntelligenceRepositoryLive,
            ),
            chClient,
            orgId,
          ),
          withAi(AIEmbedLive, getRedisClient()),
          withTracing,
        ),
      )

      return {
        datasetId: result.datasetId as string,
        versionId: result.versionId as string,
        version: result.version,
        rowCount: result.rowIds.length,
      }
    },
  )

export const addDatasetColumn = createServerFn({ method: "POST" })
  .inputValidator(z.object({ datasetId: z.string(), name: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<DatasetColumn> => {
    const orgId = await resolveOrgScope(context)

    return Effect.runPromise(
      addColumn({ datasetId: DatasetId(data.datasetId), name: data.name }).pipe(
        withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
        withTracing,
      ),
    )
  })

export const updateDatasetColumn = createServerFn({ method: "POST" })
  .inputValidator(z.object({ datasetId: z.string(), identifier: z.string().min(1), name: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<DatasetColumn> => {
    const orgId = await resolveOrgScope(context)

    return Effect.runPromise(
      updateColumn({ datasetId: DatasetId(data.datasetId), identifier: data.identifier, name: data.name }).pipe(
        withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
        withTracing,
      ),
    )
  })

export const removeDatasetColumn = createServerFn({ method: "POST" })
  .inputValidator(z.object({ datasetId: z.string(), identifier: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const orgId = await resolveOrgScope(context)

    await Effect.runPromise(
      removeColumn({ datasetId: DatasetId(data.datasetId), identifier: data.identifier }).pipe(
        withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
        withTracing,
      ),
    )
    return { ok: true }
  })

export const reorderDatasetColumns = createServerFn({ method: "POST" })
  .inputValidator(z.object({ datasetId: z.string(), order: z.array(z.string().min(1)) }))
  .handler(async ({ data, context }): Promise<DatasetColumn[]> => {
    const orgId = await resolveOrgScope(context)

    return Effect.runPromise(
      reorderColumns({ datasetId: DatasetId(data.datasetId), order: data.order }).pipe(
        withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
        withTracing,
      ),
    )
  })

export const restoreDatasetColumn = createServerFn({ method: "POST" })
  .inputValidator(z.object({ datasetId: z.string(), identifier: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<DatasetColumn> => {
    const orgId = await resolveOrgScope(context)

    return Effect.runPromise(
      restoreColumn({ datasetId: DatasetId(data.datasetId), identifier: data.identifier }).pipe(
        withScopedPostgres(DatasetRepositoryLive, getPostgresClient(), orgId),
        withTracing,
      ),
    )
  })
